import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Banknote, FileCheck2, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { AdvanceSubMethod } from "@/lib/paymentMethods";

interface OpenAccountSettleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Remaining unpaid amount, in the debt's original currency. */
  outstanding: number;
  /** Symbol for the debt's original currency (e.g. ₺). */
  currencySymbol?: string;
  onPay: (amount: number, method: AdvanceSubMethod, dueDate?: Date) => Promise<void>;
}

/**
 * Pay (part of) an Açık Hesap (open account) debt. Lets the user enter an amount
 * (defaulting to the full outstanding) and a method. Cash/card recognizes the cost
 * immediately; Çek routes it into the check lifecycle.
 */
const OpenAccountSettleDialog: React.FC<OpenAccountSettleDialogProps> = ({
  open,
  onOpenChange,
  outstanding,
  currencySymbol = "₺",
  onPay,
}) => {
  const [method, setMethod] = useState<AdvanceSubMethod | null>(null);
  const [amountStr, setAmountStr] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset to the full outstanding each time the dialog opens.
  useEffect(() => {
    if (open) {
      setAmountStr(outstanding > 0 ? String(outstanding) : "");
      setMethod(null);
      setDueDate(undefined);
    }
  }, [open, outstanding]);

  const amount = parseFloat(amountStr);
  const amountValid = !isNaN(amount) && amount > 0 && amount <= outstanding + 0.005;

  const handleConfirm = async () => {
    if (!method || !amountValid) return;
    if (method === "check" && !dueDate) return;
    setLoading(true);
    try {
      await onPay(amount, method, dueDate);
      onOpenChange(false);
    } catch {
      // surfaced by the caller's toast
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Açık Hesabı Öde</DialogTitle>
          <DialogDescription>
            Kalan borç:{" "}
            <strong>
              {currencySymbol}
              {outstanding.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
            </strong>
            . Kısmi veya tam ödeme yapabilirsiniz. Ödeme proje maliyetine eklenir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <p className="mb-1 text-[13px] text-muted-foreground">Ödeme Tutarı</p>
            <input
              type="number"
              inputMode="decimal"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              max={outstanding}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base text-gray-900"
              placeholder={String(outstanding)}
            />
            {!amountValid && amountStr !== "" && (
              <p className="mt-1 text-[12px] text-error-600">
                Tutar 0 ile kalan borç arasında olmalıdır.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMethod("cash_card")}
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-2.5 text-[13px] transition-colors",
                method === "cash_card"
                  ? "border-brand-600 bg-brand-50 text-brand-700"
                  : "border-input bg-background hover:bg-muted/50"
              )}
            >
              <Banknote className="h-4 w-4" /> Nakit / Kredi Kartı
            </button>
            <button
              type="button"
              onClick={() => setMethod("check")}
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-2.5 text-[13px] transition-colors",
                method === "check"
                  ? "border-brand-600 bg-brand-50 text-brand-700"
                  : "border-input bg-background hover:bg-muted/50"
              )}
            >
              <FileCheck2 className="h-4 w-4" /> Çek
            </button>
          </div>

          {method === "check" && (
            <div>
              <p className="mb-1 text-[13px] text-muted-foreground">Vade Tarihi</p>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "d MMMM yyyy", { locale: tr }) : "Tarih seçin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={(d) => {
                      setDueDate(d);
                      setCalendarOpen(false);
                    }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Vazgeç
          </Button>
          <Button
            type="button"
            className="flex-1 bg-brand-600 hover:bg-brand-700 text-white"
            onClick={handleConfirm}
            disabled={!method || !amountValid || (method === "check" && !dueDate) || loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ödeme Yap"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OpenAccountSettleDialog;
